<template>
  <v-main>
    <v-container fluid>
      <div class="flex items-center pl-3 mb-6">
        <div class="text-xl">{{ t("adminAdmins.title") }}</div>
        <v-spacer></v-spacer>
        <v-btn
          icon="mdi-refresh"
          variant="text"
          :loading="loading"
          :disabled="loading"
          @click="loadAdmins"
        ></v-btn>
        <v-btn color="primary" prepend-icon="mdi-plus" @click="showCreateDialog = true">
          {{ t("adminAdmins.createAdmin") }}
        </v-btn>
      </div>

      <v-data-table-virtual
        :headers="headers"
        :items="admins"
        :loading="loading"
        :sort-by="[{ key: 'createdAt', order: 'desc' }]"
        class="mt-6"
      >
        <template #[`item.createdAt`]="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>
        <template #[`item.actions`]="{ item }">
          <v-btn
            variant="text"
            color="error"
            size="small"
            prepend-icon="mdi-delete"
            :loading="deletingUid === item.uid"
            :disabled="item.uid === userStore.uid || !!deletingUid"
            @click="deleteAdminHandler(item)"
          >
            {{ t("common.delete") }}
          </v-btn>
        </template>
        <template #no-data>{{ t("adminAdmins.noAdminsFound") }}</template>
      </v-data-table-virtual>

      <v-dialog v-model="showCreateDialog" max-width="500" persistent>
        <v-card>
          <v-card-title class="flex items-center">
            <span>{{ t("adminAdmins.createAdmin") }}</span>
            <v-spacer />
            <v-btn
              icon="mdi-close"
              :aria-label="t('common.close')"
              variant="text"
              size="small"
              :disabled="creating"
              @click="cancelCreateAdminHandler"
            />
          </v-card-title>
          <v-card-text>
            <v-form
              id="create-admin-form"
              ref="createAdminForm"
              :disabled="creating"
              @submit.prevent="createAdminHandler"
            >
              <v-text-field
                v-model="newAdmin.email"
                :label="t('common.email')"
                :rules="[rules.required, rules.validEmail]"
                type="email"
              ></v-text-field>
            </v-form>
          </v-card-text>
          <v-divider />
          <v-card-actions class="px-4 py-3">
            <v-btn :disabled="creating" @click="cancelCreateAdminHandler">{{
              t("common.cancel")
            }}</v-btn>
            <v-spacer />
            <v-btn
              variant="flat"
              type="submit"
              form="create-admin-form"
              color="primary"
              :loading="creating"
              :disabled="creating"
            >
              {{ t("common.add") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { useRules } from "@/composables/useRules";
import { call } from "@/firebase";
import { useToast, useUserStore } from "@/store";
import { type CallablesMap } from "@aircap/common/callables";
import { captureException } from "@sentry/vue";
import { computed, onMounted, ref } from "vue";
import type { VForm } from "vuetify/components";

const { t, locale } = useI18n();
const { showErrorToast, showSuccessToast } = useToast();
const userStore = useUserStore();
const rules = useRules();

const loading = ref(false);
const creating = ref(false);
const deletingUid = ref<string | null>(null);
const admins = ref<CallablesMap["listAdmins"]["response"]>([]);
const showCreateDialog = ref(false);
const createAdminForm = ref<VForm | null>(null);
const newAdmin = ref({
  email: "",
});

const headers = computed(() => [
  { title: t("common.email"), key: "email", sortable: true },
  {
    title: t("adminAdmins.createdAt"),
    key: "createdAt",
    sortable: true,
    sort: (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime(),
  },
  { title: "", key: "actions", sortable: false, width: "100px" },
]);

const formatDate = (dateString: string) => {
  if (!dateString) return "-";

  const date = new Date(dateString);
  return new Intl.DateTimeFormat(locale.value, {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
};

const loadAdmins = async () => {
  loading.value = true;
  try {
    console.log("Loading admins...");
    admins.value = await call("listAdmins")({});
  } catch (cause) {
    const error = new Error("Error loading admins", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    loading.value = false;
  }
};

const createAdminHandler = async () => {
  const validateForm = await createAdminForm.value?.validate();
  if (!validateForm?.valid) return;
  creating.value = true;
  try {
    console.log("Creating admin...");
    await call("createAdmin")(newAdmin.value);
    showSuccessToast();
    showCreateDialog.value = false;
    newAdmin.value = { email: "" };
    createAdminForm.value?.reset();
    await loadAdmins();
  } catch (cause) {
    const error = new Error("Error creating admin", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    creating.value = false;
  }
};

const cancelCreateAdminHandler = () => {
  showCreateDialog.value = false;
  newAdmin.value = { email: "" };
  createAdminForm.value?.reset();
};

const deleteAdminHandler = async (admin: CallablesMap["listAdmins"]["response"][number]) => {
  if (deletingUid.value) return;
  if (!window.confirm(t("adminAdmins.deleteConfirm", { email: admin.email }))) {
    return;
  }

  deletingUid.value = admin.uid;
  try {
    console.log("Deleting admin...");
    await call("deleteAdmin")({ uid: admin.uid });
    showSuccessToast();
    await loadAdmins();
  } catch (cause) {
    const error = new Error("Error deleting admin", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    deletingUid.value = null;
  }
};

onMounted(() => {
  void loadAdmins();
});
</script>
